import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CButton, CCol, CRow } from '@coreui/react-pro'
import CandidateListTable from './CandidateListTable'
import CandidateListFilterOptions from './CandidateListFilterOptions'
import {
  customPageSize,
  initialCandidateFilterValues,
  initialPage,
  withDefault,
} from './CandidateListHelpers'
import OCard from '../../../components/ReusableComponent/OCard'
import { reduxServices } from '../../../reducers/reduxServices'
import { useAppDispatch, useTypedSelector } from '../../../stateStore'
import {
  PaginationOptionType,
  usePaginationRevised,
} from '../../../middleware/hooks/usePaginationRevised'
import OLoadingSpinner from '../../../components/ReusableComponent/OLoadingSpinner'
import { LoadingType } from '../../../types/Components/loadingScreenTypes'
import usePaginationParent from '../../../middleware/hooks/usePaginationParent'
import usePermissionToView, {
  PermissionEnum,
} from '../../../middleware/hooks/usePermissionToView'
import usePermissionAccess from '../../../middleware/hooks/usePermissionAccess'
import {
  CandidateListConfigType,
  CurrentAddCandidatePage,
  exportCandidateListProps,
  viewHandlerProps,
} from '../../../types/Recruitment/CandidateList/CandidateListTypes'
import { calculateSizeAndPage, downloadFile } from '../../../utils/helper'
import { emptyString } from '../../../constant/constantData'
import { asyncApiRetryHelper } from '../../../utils/asyncApiRetryUtils'
import useRouteHistory from '../../../middleware/hooks/useRouteHistory'

const expectedOrigin = [
  '/jobschedulecandidateList',
  '/addNewCandidate',
  '/editcandidate',
  '/candidatetimeline',
  '/jobinfo',
  '/candidateInterviewList',
  '/scheduleInterview',
]

export function CandidateListRouteLogic(): void {
  const { route } = useRouteHistory()
  const dispatch = useAppDispatch()
  const getFilterData = useTypedSelector(
    reduxServices.candidateList.selectors.getFilterData,
  )

  useEffect(() => {
    if (expectedOrigin.includes(`/${route}`)) {
      dispatch(reduxServices.candidateList.actions.setFilterData(getFilterData))
    } else {
      dispatch(
        reduxServices.candidateList.actions.setFilterData(
          initialCandidateFilterValues,
        ),
      )
    }
  }, [route])
}

const CandidateList = (): JSX.Element => {
  usePermissionToView('Candidate List', PermissionEnum.View)
  const { isPreviousRouteInHistory } = useRouteHistory() // get the route and route history
  const history = useHistory()
  const dispatch = useAppDispatch()
  const backButtonHandler = () => {
    history.goBack()
  }
  const isLoading = useTypedSelector(
    reduxServices.candidateList.selectors.isLoading,
  )
  console.log('loading state:', isLoading)

  // Track individual API loading states
  const [apiLoadingStates, setApiLoadingStates] = useState({
    empCountries: false,
    technology: false,
    employeeDetails: false,
    candidatesList: false,
  })

  const getFilterData = useTypedSelector(
    reduxServices.candidateList.selectors.getFilterData,
  )

  const isFromRelatedRoute = useTypedSelector(
    reduxServices.app.selectors.selectFromRelatedRoute,
  )

  const [filterBody, setFilterBody] = useState<viewHandlerProps>(getFilterData)

  const [dateError, setDateError] = useState<boolean>(false)

  const queryParams = new URLSearchParams(location.search)
  const jobCode = queryParams.get('jobCode')
  const jobTitle = queryParams.get('jobTitle')
  const vendorName = queryParams.get('vendorName')
  const hasQueryParams = !!(jobCode || jobTitle || vendorName)

  useEffect(() => {
    const loadInitialData = async () => {
      setApiLoadingStates({
        empCountries: true,
        technology: true,
        employeeDetails: true,
        candidatesList: true,
      })

      await Promise.allSettled([
        dispatch(reduxServices.candidateList.getEmpCountries()),
        dispatch(reduxServices.candidateList.getTechnology()),
        dispatch(reduxServices.candidateList.getAllEmployeeDetailsThunk()),
      ])

      setApiLoadingStates((prev) => ({
        ...prev,
        empCountries: false,
        technology: false,
        employeeDetails: false,
      }))

      dispatch(
        reduxServices.candidateList.actions.setCurrentAddCandidatePage(
          CurrentAddCandidatePage.addCandidate,
        ),
      )

      if (isFromRelatedRoute) {
        await reloadDataWithFilter(true)
      } else if (!hasQueryParams) {
        await reloadDataWithFilter(false)
      } else {
        setApiLoadingStates((prev) => ({ ...prev, candidatesList: false }))
      }
    }

    loadInitialData()

    return () => {
      // This is always needed to be implemented for components that uses pagination in its landing screen or as a parent component
      dispatch(reduxServices.app.actions.setFromRelatedRoute(false))
    }
  }, [])

  const listSize = useTypedSelector(
    reduxServices.candidateList.selectors.listSize,
  )

  //  const fetchData = async () => {
  //    const result = await dispatch(
  //      reduxServices.appraisalConfigurations.getCycleToEdit(Number(cycleId)),
  //    )
  //    if (result?.payload === 403) {
  //      history.push('/forbidden')
  //    }
  //  }

  const searchData = async (
    size: number,
    page: number,
    config?: CandidateListConfigType,
  ) => {
    let tempConfig = {} as CandidateListConfigType
    if (config) tempConfig = config

    const payload: viewHandlerProps = {
      ...getFilterData,
      ...tempConfig,
      startIndex: size * (page - 1),
      endIndex: size * page,
    }

    setApiLoadingStates((prev) => ({ ...prev, candidatesList: true }))

    const fetchData = async () => {
      try {
        const result = await dispatch(
          reduxServices.candidateList.getCountryWiseCandidatesList(payload),
        )
        if (result?.payload === 403) {
          history.push('/forbidden')
          setApiLoadingStates((prev) => ({ ...prev, candidatesList: false }))
          return false
        }
        const ret = result.meta.requestStatus === 'fulfilled'
        if (ret) {
          dispatch(reduxServices.candidateList.actions.setFilterData(payload))
        }
        setApiLoadingStates((prev) => ({ ...prev, candidatesList: false }))
        return ret
      } catch (error) {
        console.error('Error fetching data:', error)
        setApiLoadingStates((prev) => ({ ...prev, candidatesList: false }))
        return false
      }
    }
    await asyncApiRetryHelper(fetchData)
  }

  // This is implemented because when we have a parent component using pagination
  // and another component which is a child or related component to the parent component using pagination
  // we need to retain the pagination data of the parent component
  const reloadDataWithFilter = async (useCache = false) => {
    // This is the logic where we are setting the pagination data for this screen
    let page = !useCache ? initialPage : storedPageCache.page
    let size = !useCache ? customPageSize : storedPageCache.size
    if (isPreviousRouteInHistory('candidateInterviewList')) {
      const pagination = calculateSizeAndPage(
        getFilterData.startIndex,
        getFilterData.endIndex,
      )

      page = pagination.page ?? initialPage
      size = pagination.size ?? customPageSize
    }

    await searchData(size, page, getFilterData)
    resetPaginationAt(page, size) // This will let the pagination know the last page and size
  }

  const handleReloadDataWithFilter = () => {
    if (!hasQueryParams) {
      reloadDataWithFilter(isFromRelatedRoute)
    }
  }

  const resetCallback = handleReloadDataWithFilter

  const option: PaginationOptionType = {
    totalItemCount: listSize,
    customPageSize,
    expectedOrigin,
    initialPage,
    callback: resetCallback,
  }

  const { storedPageCache, resetPagination, resetPaginationAt } =
    usePaginationRevised(option)
  const manualCallback = handleReloadDataWithFilter
  const backButtonCondition = hasQueryParams
  usePaginationParent(
    '/jobschedulecandidateList',
    '/candidateInterviewList',
    resetPaginationAt,
    manualCallback,
  )

  const searchButtonHandler = () => {
    // also a view handler
    searchData(customPageSize, initialPage, filterBody)
    resetPagination()
  }
  const searchKeyDownButtonHandler = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      searchButtonHandler()
    }
  }

  const clearButtonHandler = () => {
    setDateError(false)
    setFilterBody(initialCandidateFilterValues)
    searchData(customPageSize, initialPage, initialCandidateFilterValues)
    resetPagination()
  }
  const permissions = usePermissionAccess()
  const userAccess = permissions.accessibility('Candidate')

  // const withDefault = <T,>(value: T | undefined | null, fallback: T): T =>
  //   value ?? fallback

  const buildExportCandidatePayload = (
    filterBody?: viewHandlerProps,
  ): exportCandidateListProps => ({
    selectionStatus: withDefault(filterBody?.selectionStatus, emptyString),
    fromDate: withDefault(filterBody?.fromDate, emptyString),
    toDate: withDefault(filterBody?.toDate, emptyString),

    searchByCandidateName: withDefault(
      filterBody?.searchByMultipleFlag,
      emptyString,
    ),
    searchBySourceName: withDefault(
      filterBody?.searchByMultipleFlag,
      emptyString,
    ),
    searchByMultipleFlag: withDefault(
      filterBody?.searchByMultipleFlag,
      emptyString,
    ),

    candidateStatus: withDefault(filterBody?.candidateStatus, 'All'),

    selectionTechnology: withDefault(
      filterBody?.selectionTechnology,
      emptyString,
    ),
    searchByExperience: withDefault(filterBody?.experience, emptyString),

    selectionCountry: withDefault(filterBody?.selectionCountry, ''),

    searchByRecruiterName: withDefault(filterBody?.recruiter, emptyString),
    recruiter: withDefault(filterBody?.recruiter, emptyString),
    experience: withDefault(filterBody?.experience, emptyString),

    interviewRound: withDefault(filterBody?.interviewRound, emptyString),
    isResumeMatcher: withDefault(filterBody?.isResumeMatcher, emptyString),
    isExternalCreated: withDefault(filterBody?.isExternalCreated, 'All'),
    appliedFor: withDefault(filterBody?.appliedFor, emptyString),
  })

  const handleExportCandidate = async () => {
    const payload = buildExportCandidatePayload(filterBody)
    console.log('', payload)

    const exportCandidateBlob = await dispatch(
      reduxServices.candidateList.exportCandidateList(payload),
    )

    downloadFile(exportCandidateBlob?.payload as Blob, 'CandidateList.csv')
  }

  const allApisLoaded = !Object.values(apiLoadingStates).some(
    (state) => state === true,
  )

  return (
    <>
      <OCard
        className="mb-4 myprofile-wrapper"
        title="Candidate List"
        CBodyClassName="ps-0 pe-0"
        CFooterClassName="d-none"
      >
        {backButtonCondition && (
          <CRow className="justify-content-end">
            <CCol className="text-end" md={4}>
              <CButton
                color="primary"
                className="btn-ovh me-1"
                data-testid="back-button"
                onClick={backButtonHandler}
              >
                <i className="fa fa-arrow-left  me-1"></i>Back
              </CButton>
            </CCol>
          </CRow>
        )}
        <CandidateListFilterOptions
          searchKeyDownButtonHandler={searchKeyDownButtonHandler}
          searchButtonHandler={searchButtonHandler}
          clearButtonHandler={clearButtonHandler}
          userAccess={userAccess}
          handleExportCandidate={handleExportCandidate}
          filterBody={filterBody}
          setFilterBody={setFilterBody}
          dateError={dateError}
          setDateError={setDateError}
        />
        {!allApisLoaded ? (
          <div className="mt-4">
            <OLoadingSpinner type={LoadingType.PAGE} />
          </div>
        ) : (
          <CandidateListTable
            currentPage={storedPageCache.page}
            pageSize={storedPageCache.size}
            searchInput={filterBody?.searchByMultipleFlag}
            userAccess={userAccess}
            searchData={searchData}
          />
        )}
      </OCard>
    </>
  )
}

export default CandidateList
